
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Trophy, Zap, CheckCircle2, Clock, Target, ArrowRight, Star } from 'lucide-react';
import CTAButton from '@/components/CTAButton';

const FinalOfferCTASection = () => {
  const { isMobile } = useBreakpoint();

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-[#003a70]/5 via-white to-[#FEE6D8]/20" 
      paddingY="py-12 md:py-16 lg:py-24"
      containerWidth="wide"
      backgroundPattern="dots"
      patternColor="#003a70"
      patternOpacity={3}
    >
      <div className="text-center space-y-8 md:space-y-12 lg:space-y-16">
        
        {/* Urgency Badge */}
        <div className="space-y-6 md:space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-100 to-red-50 rounded-full shadow-lg border-2 border-red-200">
            <Clock className="text-red-600" size={isMobile ? 20 : 24} />
            <span className="text-red-600 font-bold text-sm md:text-lg lg:text-xl">Limited Time Offer</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto px-4",
            isMobile ? "text-2xl" : "text-3xl md:text-4xl lg:text-5xl"
          )}>
            Get Everything You Need to Start
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
              Selling AI Business Cards Today
            </span>
          </h2>
        </div>

        {/* Main Value Proposition */}
        <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-10 border-2 border-[#FEE6D8]/60 shadow-xl max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4 md:mb-6">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                <Star className="text-white" size={isMobile ? 20 : 28} />
              </div>
            </div>
            
            <h3 className={cn(
              "text-[#003a70] font-bold leading-tight mb-4 md:mb-6",
              isMobile ? "text-lg" : "text-xl md:text-2xl lg:text-3xl"
            )}>
              Complete Done-For-You Package
            </h3>
            
            <div className="space-y-3 md:space-y-4 text-left">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-[#003a70] flex-shrink-0 mt-1" size={isMobile ? 16 : 20} />
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-sm" : "text-base md:text-lg"
                )}>
                  Professional landing page template (ready to customize)
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-[#003a70] flex-shrink-0 mt-1" size={isMobile ? 16 : 20} />
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-sm" : "text-base md:text-lg"
                )}>
                  White-label video sales letters (multiple versions)
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-[#003a70] flex-shrink-0 mt-1" size={isMobile ? 16 : 20} />
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-sm" : "text-base md:text-lg"
                )}>
                  6 industry-specific AI business card templates
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-4 md:space-y-6 animate-fade-in px-4" style={{ animationDelay: '0.6s' }}>
          <div className="max-w-md mx-auto">
            <CTAButton 
              variant="accent" 
              to="https://cart.deliciousmarketing.io/genius-card-playbook-dfy/?coupon=DFY-LAUNCH" 
              className={cn(
                "w-full text-[#003a70] font-bold shadow-2xl hover:shadow-xl transition-all duration-300 border-2 border-[#FEE6D8]/50",
                isMobile ? "text-base py-4 px-6 rounded-xl" : "text-lg md:text-xl py-5 md:py-6 px-8 md:px-10 rounded-2xl"
              )}
            >
              <span className="flex items-center justify-center gap-2 md:gap-3">
                {isMobile ? "Get DFY Bundle Now" : "Get Complete DFY Bundle"}
                <ArrowRight size={isMobile ? 18 : 24} />
              </span>
            </CTAButton>
          </div>
          
          <p className={cn(
            "text-[#003a70]/70 leading-relaxed max-w-2xl mx-auto",
            isMobile ? "text-xs px-2" : "text-sm md:text-base"
          )}>
            Everything you need to start selling AI business cards professionally - 
            no design skills or copywriting required
          </p>
        </div>

        {/* Final Motivation */}
        <div className="space-y-6 md:space-y-8 animate-fade-in px-4" style={{ animationDelay: '0.8s' }}>
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-[#003a70]/10 to-[#003a70]/5 rounded-2xl p-6 md:p-8 border border-[#003a70]/20 shadow-lg">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Trophy className="text-[#003a70]" size={isMobile ? 28 : 36} />
              </div>
              
              <p className={cn(
                "text-[#003a70] font-bold leading-relaxed",
                isMobile ? "text-base" : "text-lg md:text-xl lg:text-2xl"
              )}>
                Skip the guesswork. Skip the delays.
                <br />
                <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
                  Start selling tomorrow.
                </span>
              </p>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default FinalOfferCTASection;
